/**
 * @fileoverview <MarketingLanguage> — the language a Colony marketing site shows
 * (LIFE-SHORTLISTED-001 F-Q7-2, WCAG 3.1.1).
 *
 * These sites translate their pages with their chrome, so the language in force IS each page's
 * language. This component, mounted by `createMarketingApp` inside its `I18nextProvider`, makes that
 * true in both places it is announced:
 *   - `<html lang>` / `dir`, through `useSyncHtmlLang` (`@puffin-core/i18n-bootstrap`), the one writer;
 *   - each page's WebPage JSON-LD, through `useMarketingPageLanguage`, which `<MarketingSeo>` reads.
 *
 * It is scoped to the factory on purpose. A tree that merely sits under an i18n provider does not
 * translate its content: the tenant site's CMS pages are written in one language whatever its chrome
 * shows, so outside a marketing site the page language is `null` and a page names its own or none.
 *
 * @module @puffin-core/marketing-shell/i18n/MarketingLanguage
 */

import { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSyncHtmlLang } from '@puffin-core/i18n-bootstrap';

const MarketingPageLanguageContext = createContext(null);

/**
 * @param {Object} props
 * @param {Object} props.i18n - the site's i18next instance (the one its `I18nextProvider` holds)
 * @param {React.ReactNode} props.children
 * @returns {React.JSX.Element}
 */
export function MarketingLanguage({ i18n, children }) {
  useSyncHtmlLang(i18n);
  // `useTranslation` re-renders this provider on every `languageChanged`, so the pages follow.
  const { i18n: active } = useTranslation();
  const language = active.resolvedLanguage || active.language;
  return (
    <MarketingPageLanguageContext.Provider value={language}>
      {children}
    </MarketingPageLanguageContext.Provider>
  );
}

MarketingLanguage.propTypes = {
  i18n: PropTypes.object.isRequired,
  children: PropTypes.node,
};

/**
 * @returns {?string} the language the marketing site's pages show, or `null` outside one
 */
export function useMarketingPageLanguage() {
  return useContext(MarketingPageLanguageContext);
}
